import { clampPlan, DEFAULT_PLAN } from '../plan'
import type { Plan } from '../plan'
import { DEFAULT_SCENARIO_SET, makeScenario, withSharedAge } from '../scenarios'
import type { Scenario, ScenarioSet } from '../scenarios'

/**
 * Encodes the scenario set into the URL hash, and reads it back.
 *
 * This is the whole sharing story, and it costs nothing: no accounts, no
 * stored state, and nothing on a server. (F6a added one endpoint, for email
 * capture; it has no access to a plan, and this file has no access to it.) Combined with the fixed `SHARED_SEED` in `useSimulation`, a
 * pasted link reproduces the sender's chart exactly — including the Monte Carlo
 * paths — so nobody opens a thread asking why their number differs from the
 * screenshot.
 *
 * It also doubles as "save my plan": the address bar is the save file.
 *
 * Format:  #v3.w.1e.2gosc.255s.qglc.balanced.1f4[.<six more for scenario B>]
 *          ^^^ ^ ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
 *          |   |  scenario A: retire-by, corpus, monthly, expenses, profile, inflation
 *          |   current age — shared, because you are one age
 *          version
 *
 * Numbers are base-36 to keep the link short enough to survive WhatsApp and
 * Reddit without wrapping; a two-scenario link lands around 75 characters. The
 * profile id stays human-readable: it is the one field where a wrong value
 * silently changes the answer rather than obviously breaking it.
 *
 * **The active scenario is not encoded.** Which one you were editing is UI state,
 * not a property of the plan, and a link always opens on A.
 */

const VERSION = 'v3'
const SEPARATOR = '.'
/** currentAge. Shared across scenarios — see `withSharedAge`. */
const SHARED_FIELDS = 1
/** fireByAge, corpus, monthly, expenses, profileId, inflation. */
const PER_SCENARIO = 6

/**
 * Formats this codec has spoken, with the field count each one carried.
 *
 * v1 predates the inflation slider; v2 predates scenarios. Old links stay
 * readable and inherit sensible blanks — a shared link that stops working is
 * worse than one that fills in a default, and the whole point of putting the plan
 * in the URL is that the URL keeps working.
 */
const LEGACY_FIELD_COUNTS: Record<string, number> = { v1: 6, v2: 7 }

/** Inflation is a small decimal, so it travels as basis points: 0.05 -> 500. */
const BPS = 10_000

/** Rupee amounts carry no meaningful sub-rupee precision, so round before encoding. */
function encodeNumber(value: number): string {
  return Math.round(value).toString(36)
}

function decodeNumber(token: string): number | null {
  // parseInt is deliberately lenient about trailing junk, so validate the shape
  // first: a hand-edited '1e-notavalue' must fail, not silently become 1e.
  if (token === undefined || !/^-?[0-9a-z]+$/.test(token)) return null
  const value = Number.parseInt(token, 36)
  return Number.isFinite(value) ? value : null
}

/** The six per-scenario tokens, in order. */
function scenarioTokens(plan: Plan): string[] {
  const safe = clampPlan(plan)
  return [
    encodeNumber(safe.fireByAge),
    encodeNumber(safe.currentCorpus),
    encodeNumber(safe.monthlyInvestment),
    encodeNumber(safe.annualExpensesToday),
    safe.profileId,
    encodeNumber(safe.inflationMean * BPS),
  ]
}

/** Reads six tokens back into a plan, given the shared age. Null if unreadable. */
function planFromTokens(tokens: string[], currentAge: number): Plan | null {
  const fireByAge = decodeNumber(tokens[0])
  const currentCorpus = decodeNumber(tokens[1])
  const monthlyInvestment = decodeNumber(tokens[2])
  const annualExpensesToday = decodeNumber(tokens[3])
  const profileId = tokens[4]
  const inflationBps = decodeNumber(tokens[5])

  if (
    fireByAge === null ||
    currentCorpus === null ||
    monthlyInvestment === null ||
    annualExpensesToday === null ||
    profileId === undefined ||
    profileId === '' ||
    inflationBps === null
  ) {
    return null
  }

  // clampPlan also replaces an unknown profile id with the default, so a link
  // from a future build that adds a profile still renders something sensible.
  return clampPlan({
    currentAge,
    fireByAge,
    currentCorpus,
    monthlyInvestment,
    annualExpensesToday,
    profileId,
    inflationMean: inflationBps / BPS,
  })
}

/** Serialises a scenario set into a hash fragment, including the leading '#'. */
export function encodeScenarios(set: ScenarioSet): string {
  const scenarios = withSharedAge(set.scenarios)
  const currentAge = clampPlan(scenarios[0].plan).currentAge
  const tokens = [VERSION, encodeNumber(currentAge)]
  for (const scenario of scenarios) tokens.push(...scenarioTokens(scenario.plan))
  return '#' + tokens.join(SEPARATOR)
}

/**
 * Reads a scenario set back out of a hash fragment.
 *
 * Returns `null` for anything it cannot read with confidence — a different
 * version, the wrong field count, an unparseable number. It never throws and
 * never partially applies: a half-understood link would put a chart on screen
 * that is not the one the sender saw, which is worse than falling back to
 * defaults, because it looks like it worked.
 *
 * A successfully parsed plan is still clamped, because the hash is untrusted
 * input that flows into typed-array allocation sizes.
 */
export function decodeScenarios(hash: string): ScenarioSet | null {
  const body = hash.startsWith('#') ? hash.slice(1) : hash
  if (body === '') return null

  const parts = body.split(SEPARATOR)
  const version = parts[0]

  if (version !== VERSION) {
    // v1 and v2 carried exactly one plan. They open as a single scenario, with
    // compare off — which is also the arrival state, so an old link behaves
    // precisely as it did before F4.
    const plan = decodePlan(hash)
    if (plan === null) return null
    return { scenarios: [makeScenario('a', plan)], activeId: 'a' }
  }

  const fields = parts.length - 1 - SHARED_FIELDS
  if (fields !== PER_SCENARIO && fields !== PER_SCENARIO * 2) return null

  const currentAge = decodeNumber(parts[1])
  if (currentAge === null) return null

  const ids = ['a', 'b'] as const
  const scenarios: Scenario[] = []
  for (let i = 0; i * PER_SCENARIO < fields; i++) {
    const start = 1 + SHARED_FIELDS + i * PER_SCENARIO
    const plan = planFromTokens(parts.slice(start, start + PER_SCENARIO), currentAge)
    if (plan === null) return null
    scenarios.push(makeScenario(ids[i], plan))
  }

  return { scenarios, activeId: 'a' }
}

/**
 * Reads a single plan out of a v1 or v2 fragment.
 *
 * Kept as its own function rather than folded into `decodeScenarios` because the
 * legacy formats have their own field layout — `currentAge` sits inside the plan
 * there, not beside it — and interleaving the two shapes in one parser is how a
 * malformed old link ends up silently decoding as a new one.
 */
export function decodePlan(hash: string): Plan | null {
  const body = hash.startsWith('#') ? hash.slice(1) : hash
  if (body === '') return null

  const parts = body.split(SEPARATOR)
  const expectedFields = LEGACY_FIELD_COUNTS[parts[0]] ?? null
  if (expectedFields === null) return null
  if (parts.length !== expectedFields + 1) return null

  const currentAge = decodeNumber(parts[1])
  const fireByAge = decodeNumber(parts[2])
  const currentCorpus = decodeNumber(parts[3])
  const monthlyInvestment = decodeNumber(parts[4])
  const annualExpensesToday = decodeNumber(parts[5])
  const profileId = parts[6]

  // Absent in v1 links, which were written before inflation was a control.
  const inflationBps = parts[7] === undefined ? null : decodeNumber(parts[7])
  const inflationMean =
    inflationBps === null ? DEFAULT_PLAN.inflationMean : inflationBps / BPS

  if (
    currentAge === null ||
    fireByAge === null ||
    currentCorpus === null ||
    monthlyInvestment === null ||
    annualExpensesToday === null ||
    profileId === undefined ||
    profileId === '' ||
    // A v2 link with an unreadable inflation field is corrupt, not old. Only a
    // genuinely absent field earns the default.
    (parts[0] === 'v2' && inflationBps === null)
  ) {
    return null
  }

  return clampPlan({
    currentAge,
    fireByAge,
    currentCorpus,
    monthlyInvestment,
    annualExpensesToday,
    profileId,
    inflationMean,
  })
}

/** Reads the current location hash, falling back to the defaults. */
export function readScenariosFromLocation(): ScenarioSet {
  if (typeof window === 'undefined') return DEFAULT_SCENARIO_SET
  return decodeScenarios(window.location.hash) ?? DEFAULT_SCENARIO_SET
}
